import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDrag } from "@use-gesture/react";
import { toast } from "react-toastify";
import { StyledSingleReservationScreen, HeaderWrapper } from "./styles";

import { IoTDevice, Reservation } from "../../types/reservation";
import type { DoorReservationAction } from "../../hooks/useUnlockUnit";
import { formatDateWithOrdinal } from "../../utils/formatDateWithOrdinals";
import assets from "../../assets/index";
import RentalAgreement from "../../components/RentalAgreement";
import {
  Button,
  ErrorScreen,
  Header,
  LoaderComponent,
} from "../../components/index";

import {
  useUnlockUnit,
  useWeather,
  useLocationTime,
  useReservationDetails,
  useScroll,
  usePreRegister,
} from "../../hooks";

const BoxContent = [
  {
    id: "about0",
    label: "About Unit",
    icon: "🛋️",
    description: "Check out unit details here.",
    dataKey: "allocatedUnits",
  },
  {
    id: "parking1",
    label: "Parking",
    icon: "🅿️",
    description: "Check out parking details here.",
    dataKey: "allocatedUnits",
  },
  {
    id: "checkin3",
    label: "Check In",
    icon: "🔑",
    description: "When and how can I check in?",
    dataKey: "allocatedUnits",
  },
  {
    id: "preregistered",
    label: "Pre-registration",
    icon: "✔️",
    description: "Complete your registration before arrival",
    dataKey: "allocatedUnits",
  },
  {
    id: "checkout4",
    label: "Check Out",
    icon: "👋🏻",
    description: "What's the checkout process?",
    dataKey: "allocatedUnits",
  },
  {
    id: "wifi5",
    label: "WI-FI",
    icon: "🛜",
    description: "See WiFi details here.",
    dataKey: "allocatedUnits",
  },
  {
    id: "ameneties6",
    label: "Amenities",
    icon: "🛁",
    description: "See the Amenities at your disposal.",
    dataKey: "allocatedUnits",
  },
];

const SingleReservationScreen = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isPreRegistering, setIsPreRegistering] = useState(false);
  const { id } = useParams<{ id: string }>();
  const {
    reservation: reservationData,
    loading,
    error,
  } = useReservationDetails(id || "");
  const navigate = useNavigate();

  const {
    loading: isUnlockingLoading,
    activeLockUid,
    errorMessage,
    isDoorUnlocked,
    setDoorReservation,
  } = useUnlockUnit();

  const isDoorDevice = (device: IoTDevice) => device.type === "DOOR";
  const canUnlockDoor = (device: IoTDevice) =>
    isDoorDevice(device) && device.supportsUnlock === true;
  const canLockDoor = (device: IoTDevice) =>
    isDoorDevice(device) && device.supportsLock === true;

  const getSupportedDoorActions = (
    device: IoTDevice
  ): DoorReservationAction[] => {
    const actions: DoorReservationAction[] = [];
    if (canUnlockDoor(device)) actions.push("unlock");
    if (canLockDoor(device)) actions.push("lock");
    return actions;
  };

  const getNextDoorAction = (
    device?: IoTDevice | null
  ): DoorReservationAction | null => {
    if (!device) return null;

    const supportedActions = getSupportedDoorActions(device);
    const canUnlock = supportedActions.includes("unlock");
    const canLock = supportedActions.includes("lock");

    if (canUnlock && canLock) {
      return isDoorUnlocked(device.uid) ? "lock" : "unlock";
    }

    if (canUnlock) return "unlock";
    if (canLock) return "lock";

    return null;
  };

  const doorDevices =
    reservationData?.iotDevices?.filter((device) => isDoorDevice(device)) || [];
  const doorLocks = doorDevices.filter(
    (device) => getSupportedDoorActions(device).length > 0
  );
  const hasMultipleLocks = doorLocks.length > 1;

  const primaryDoorLock = doorLocks[0];
  const primaryDoorAction = getNextDoorAction(primaryDoorLock);

  const {
    loading: isPreRegisterLoading,
    isPreRegistered,
    preRegister,
  } = usePreRegister();

  const getDoorActionLabel = (action: DoorReservationAction) =>
    action === "lock" ? "Lock" : "Unlock";

  const getDoorActionSuccessMessage = (action: DoorReservationAction) =>
    action === "lock"
      ? "🔒 Door successfully locked."
      : "🔓 Door successfully unlocked! Welcome!";

  const handleDoorAction = async (
    lock: IoTDevice,
    action: DoorReservationAction
  ) => {
    if (!lock.uid || !reservationData?.uid) {
      console.error("Lock UID (IoT device) or Reservation UID is missing");
      toast.error("Door action failed: Missing information.");
      return;
    }
    if (!getSupportedDoorActions(lock).includes(action)) {
      const unsupportedMessage =
        action === "lock"
          ? "Remote lock is not supported for this door."
          : "Remote unlock is not supported for this door.";
      toast.error(unsupportedMessage, {
        style: {
          background: "#EF6469",
          color: "#ffffff",
          fontSize: "12px",
        },
        icon: false,
        closeButton: false,
      });
      return;
    }

    setIsUnlocking(true); // start showing spinner

    try {
      await setDoorReservation(lock.uid, reservationData.uid, action);
      toast.success(getDoorActionSuccessMessage(action), {
        style: {
          background: "#3FC380",
          color: "#ffffff",
          fontSize: "12px",
        },
        icon: false,
        closeButton: false,
      });
    } catch (error) {
      console.error(`Door ${action} failed:`, error);

      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : `❌ Failed to ${action} the door. Please try again.`;

      toast.error(errorMessage, {
        style: {
          background: "#EF6469",
          color: "#ffffff",
          fontSize: "12px",
        },
        icon: false,
        closeButton: false,
      });
    } finally {
      setIsUnlocking(false); // always hide spinner after
      setShowDropdown(false);
    }
  };

  const handlePreRegister = async () => {
    if (!reservationData?.uid) {
      console.error("Reservation UID is missing");
      toast.error("Pre-registration failed: Missing information.");
      return;
    }

    setIsPreRegistering(true);

    try {
      await preRegister(reservationData.uid);
      toast.success("✅ Pre-registration completed successfully!", {
        style: {
          background: "#3FC380",
          color: "#ffffff",
          fontSize: "12px",
        },
        icon: false,
        closeButton: false,
      });
    } catch (error) {
      console.error("Pre-registration failed:", error);

      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : "❌ Failed to complete pre-registration. Please try again.";

      toast.error(errorMessage, {
        style: {
          background: "#EF6469",
          color: "#ffffff",
          fontSize: "12px",
        },
        icon: false,
        closeButton: false,
      });
    } finally {
      setIsPreRegistering(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const isScrolled = useScroll();
  const [expandedBox, setExpandedBox] = useState<string | null>(null);

  const { city, isNight } = useLocationTime(
    reservationData?.lat || 0,
    reservationData?.lon || 0
  );

  const { weather, emoji, description, advice } = useWeather(
    reservationData?.lat,
    reservationData?.lon,
    isNight
  );

  const bind = useDrag(({ swipe: [swipeX, swipeY] }) => {
    if (swipeY === 1) {
      setExpandedBox(null);
    }
  });

  const handleCopyWifiPassword = () => {
    const wifiPassword =
      reservationData?.allocatedUnits[0]?.wifiPassword ||
      "No password available";
    navigator.clipboard.writeText(wifiPassword).then(() => {
      alert("Wi-Fi Password copied to clipboard!");
    });
  };

  const handleBoxClick = (boxName: string) => {
    setExpandedBox((prev) => (prev === boxName ? null : boxName));
  };

  const getMainImageFromAllocatedUnits = (reservationData: Reservation) => {
    const allocatedUnit = reservationData.allocatedUnits?.[0];
    if (allocatedUnit?.mainImage?.image) {
      const imageUrl = allocatedUnit.mainImage.image.urls?.big;
      return imageUrl;
    } else {
      return "";
    }
  };

  const isPastReservation = (reservationData: Reservation) => {
    const { fromDate, toDate } = reservationData;
    const today = new Date();
    const fromTime = new Date(fromDate).getTime();
    const toTime = new Date(toDate).getTime();
    const todayTime = today.getTime();
    const fromDateBeforeToday = fromTime < todayTime;
    const toDateBeforeToday = toTime < todayTime;
    return fromDateBeforeToday && toDateBeforeToday;
  };

  const getNumOfNights = (reservationData: Reservation) => {
    const numOfNights = Math.floor(
      (Date.parse(reservationData.toDate) -
        Date.parse(reservationData.fromDate)) /
        86400000
    );

    return numOfNights === 1 ? "1 night" : `${numOfNights} nights`;
  };
  if (loading) {
    return <LoaderComponent content="Reservation details loading ..." />;
  }

  if (error || !reservationData)
    return (
      <ErrorScreen
        content={errorMessage || "An error happened.Try again"}
        imageSrc={assets.NoResultsVector}
      />
    );

  if (reservationData) {
    return (
      <>
        <HeaderWrapper>
          <Header />
          <div style={{ height: 40 }}></div>
        </HeaderWrapper>
        <StyledSingleReservationScreen className={isScrolled ? "scrolled" : ""}>
          <img
            className="cover-photo"
            style={
              isPastReservation(reservationData)
                ? { filter: "grayscale(1)" }
                : { filter: "initial" }
            }
            src={getMainImageFromAllocatedUnits(reservationData) || ""}
            onError={(e) => {
              e.currentTarget.src = assets.NoResultsVector;
            }}
            alt=""
          />
          <div className="go-back circle" onClick={() => navigate(-1)}>
            <img src={assets.GoBack} alt="" />
          </div>

          <div className="info-section">
            <span>Ref.code: {reservationData?.referenceCode}</span>
            <div className="flex-div">
              <h2>{reservationData?.allocatedUnits?.[0]?.name}</h2>
              {isPastReservation(reservationData) && (
                <div className="tab">Expired</div>
              )}
            </div>
            <div className="flex-div">
              <p>
                {reservationData.adults}{" "}
                {reservationData.adults === 1 ? "adult " : "adults "},{" "}
                {reservationData.children}{" "}
                {reservationData.children === 1 ? "child " : "children "}
              </p>

              <p>
                {getNumOfNights(reservationData)}{" "}
                <span className="bullet-separator">• </span>(
                {formatDateWithOrdinal(reservationData.fromDate)} -{" "}
                {formatDateWithOrdinal(reservationData.toDate)})
              </p>
            </div>
            {!isPastReservation(reservationData) &&
              reservationData.accessCode?.code && (
                <div className="access-code">
                  <span>
                    🔑 If you experience issues unlocking the door, please use
                    the following access code:{" "}
                    <strong>{reservationData.accessCode.code}</strong>
                  </span>{" "}
                </div>
              )}
            {!isPastReservation(reservationData) && (
              <div className="weather-box">
                <p>{city}</p>
                {emoji && <div className="emoji"> {emoji}</div>}
                {weather && <div className="weather-deg">{weather}</div>}

                <p>{description}</p>
              </div>
            )}
            {!isPastReservation(reservationData) && (
              <>
                {doorLocks.length === 0 ? (
                  <Button className="unlock" disabled>
                    {doorDevices.length === 0
                      ? "No smart lock detected"
                      : "Remote lock unavailable"}
                  </Button>
                ) : hasMultipleLocks ? (
                  <div className="dropdown-container">
                    <Button
                      className="unlock"
                      onClick={() => setShowDropdown(!showDropdown)}
                      disabled={isUnlockingLoading || isUnlocking}
                    >
                      {isUnlocking ? (
                        <span style={{ fontSize: 12 }}>Loading...</span>
                      ) : (
                        <>
                          <span>🔑</span> Manage Locks
                        </>
                      )}
                    </Button>
                    {showDropdown && (
                      <ul className="dropdown-menu">
                        {doorLocks.map((lock) => {
                          const action = getNextDoorAction(lock);
                          if (!action) return null;

                          const label = getDoorActionLabel(action);

                          return (
                            <li
                              key={lock.uid}
                              className={`dropdown-item${
                                activeLockUid === lock.uid ? " loading" : ""
                              }`}
                              onClick={() => {
                                if (isUnlocking || isUnlockingLoading) return;
                                handleDoorAction(lock, action);
                              }}
                            >
                              {activeLockUid === lock.uid
                                ? "Loading..."
                                : `${label} ${lock.name}`}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Button
                    className="unlock"
                    onClick={() => {
                      if (!primaryDoorLock || !primaryDoorAction) return;
                      handleDoorAction(primaryDoorLock, primaryDoorAction);
                    }}
                    disabled={
                      isUnlockingLoading || isUnlocking || !primaryDoorAction
                    }
                  >
                    {isUnlocking ? (
                      <span style={{ fontSize: 12 }}>Loading...</span>
                    ) : !primaryDoorAction ? (
                      "Remote lock unavailable"
                    ) : (
                      <>
                        <span>{primaryDoorAction === "lock" ? "🔒" : "🔑"}</span>{" "}
                        {getDoorActionLabel(primaryDoorAction)} Unit
                      </>
                    )}
                  </Button>
                )}
              </>
            )}
            {!isPastReservation(reservationData) && advice && (
              <div className="advice-box">
                <div>
                  <h2>☂️</h2>
                  <p>{advice}</p>
                </div>
              </div>
            )}

            <RentalAgreement key={reservationData.uid} reservationUid={reservationData.uid} />
            <div className="grid-div">
              {BoxContent.map((cont) => {
                if (cont.id === "preregistered" && reservationData?.sourceChannel !== "visual_matrix") {
                  return null;
                }
                if (cont.id === "preregistered" && reservationData?.checkInOnline === true) {
                  return (
                     <div
                        key={cont.id}
                        aria-expanded
                        {...bind()}
                        className="box"
                      >
                        <span>✅</span>
                        <div className="title">
                          {cont.label}
                        </div>
                        <p>Pre-Registration Complete</p>
                      </div>
                  );
                }
                return (
                <div
                  key={cont.id}
                  aria-expanded
                  {...bind()}
                  className={`box ${expandedBox === cont.id ? "expanded" : ""}`}
                  onClick={() => handleBoxClick(cont.id)}
                >
                  {expandedBox !== cont.id && <span>{cont.icon}</span>}
                  <div className="title">
                    {expandedBox === cont.id && (
                      <img
                        alt=""
                        src={assets.GoBack}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedBox(null);
                        }}
                      />
                    )}{" "}
                    {cont.label}
                  </div>
                  {expandedBox !== cont.id && <p>{cont.description}</p>}
                  {expandedBox === cont.id && (
                    <div className="expanded-content">
                      {cont.id === "about0" && (
                        <div>
                          {reservationData.allocatedUnits[0].description ? (
                            reservationData.allocatedUnits[0].description
                          ) : (
                            <div className="vertical-flex">
                              <img
                                height={120}
                                src={assets.NoResultsVector}
                                alt="No unit description provided"
                              />
                              <p style={{ margin: 0 }}>
                                No unit description available.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      {cont.id === "parking1" && (
                        <p>
                          {reservationData.allocatedUnits[0]
                            .inUseParkingInformation ||
                            "No parking details available"}
                        </p>
                      )}
                      {cont.id === "checkin3" && (
                        <p>
                          {reservationData.allocatedUnits[0]
                            .inUseCheckInInstructions ||
                            "No check-in details available"}
                        </p>
                      )}
                      {cont.id === "preregistered" && (
                        <div>
                          {reservationData.checkInOnline === true ? (
                              <p style={{ marginBottom: 16 }}>
                                Pre-Registration Complete
                              </p>
                            ): 
                            <>
                              <p style={{ marginBottom: 16 }}>
                                Complete your registration before arrival to speed up
                                your check-in process. This will confirm your details
                                with our system.
                              </p>
                              <Button
                                onClick={handlePreRegister}
                                disabled={
                                  isPreRegisterLoading ||
                                  isPreRegistered ||
                                  isPreRegistering
                                }
                                style={{
                                  width: "100%",
                                  padding: "12px",
                                  fontSize: "14px",
                                }}
                              >
                                {isPreRegistering ? (
                                  <span style={{ fontSize: 12 }}>Processing...</span>
                                ) : (
                                  <>
                                    
                                    {isPreRegistered
                                      ? "Pre-Registration Complete!"
                                      : "Confirm Pre-Registration"}
                                  </>
                                )}
                              </Button>
                            </>
                          }
                          
                        </div>
                      )}
                      {cont.id === "checkout4" && (
                        <p>
                          {reservationData.allocatedUnits[0]
                            .inUseCheckOutInstructions ||
                            "No check-out details available"}
                        </p>
                      )}
                      {cont.id === "wifi5" && (
                        <>
                          <p style={{ fontWeight: 600, marginBottom: 6 }}>
                            WiFi Network{" "}
                          </p>
                          <p>
                            {" "}
                            {reservationData.allocatedUnits[0].wifiNetwork ||
                              "🚫  No Wi-Fi Network available"}
                          </p>

                          <p style={{ fontWeight: 600, marginBottom: 6 }}>
                            WiFi Password{" "}
                          </p>

                          {reservationData.allocatedUnits[0].wifiPassword ? (
                            <div className="password-btn">
                              <div>
                                {reservationData.allocatedUnits[0].wifiPassword}{" "}
                              </div>{" "}
                              <div
                                className="copy-btn"
                                onClick={handleCopyWifiPassword}
                              >
                                <img
                                  src={assets.CopyVector}
                                  alt="copy-password"
                                />
                              </div>
                            </div>
                          ) : (
                            "🚫  No Wi-Fi Password available"
                          )}
                        </>
                      )}
                      {cont.id === "ameneties6" && (
                        <div style={{ height: "100%" }}>
                          {reservationData.allocatedUnits[0].inUseAmenities?.map(
                            (a) => (
                              <ul>
                                <li
                                  style={{ textTransform: "capitalize" }}
                                  key={a.id}
                                >
                                  {a.title} <br /> {a.description}
                                </li>
                              </ul>
                            )
                          ) || "No amenities available"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            </div>
          </div>
        </StyledSingleReservationScreen>
      </>
    );
  }

  return <StyledSingleReservationScreen></StyledSingleReservationScreen>;
};

export default SingleReservationScreen;
